import type { FC } from 'react';
import React from 'react';
import { Element, scroller } from 'react-scroll';
import styled from 'styled-components';

import CheckoutExtras from '@lt/components/components/CheckoutAdditionalServices/CheckoutExtras';
import CheckoutTransfers from '@lt/components/components/CheckoutAdditionalServices/CheckoutTransfer';

import { useTranslation } from '@lt/localization';
import JointOrder from 'checkout/components/JointOrder';
import CancellationPolicyContainer from '../../../../components/CancellationPolicyContainer';

import { ExtraComment } from '../../../../components/ExtraComment';
import ServiceLayout from '../../components/ServiceLayout';

import ExtraInsurance from '../../../../components/ExtraInsurance';
import PartnerLoyalty from '../../components/PartnerLoyalty';
import ExtraItems from './components/ExtraItems';
import UpgradeFlightClass from './components/UpgradeFlightClass';

import type { IServices } from './types';

const Services = (props: IServices): JSX.Element => {
  const {
    ltExtras,
    tourExtras,
    toggleExtras,
    saveComment,
    comment,
    transferList,
    extrasList,
    setOptions,
    toggleInsurance,
    isSelectedInsurance,
    cancellationPolicy,
    isMobile,
    isHotelOnly,
    chosenUpsales,
    uncheckOption,
  } = props;

  const ensuranceBtnClickHandler = () => {
    scroller.scrollTo('ensurance-widget', {
      duration: 800,
      delay: 0,
      smooth: 'easeInOutQuart',
    });
    toggleInsurance();
  };

  const hasTransfer = Boolean(transferList.length > 0);
  const hasExtras = Boolean(extrasList.length > 0);
  const hasTourExtras = Boolean(tourExtras.length > 0);
  const hasLtExtras = Boolean(ltExtras.length > 0);
  const hasCancellationPolicy = Boolean(cancellationPolicy);

  // TODO: remove this line when translate UpgradeFlightClass to Typescript
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const UpgradeFlightClassComponent = UpgradeFlightClass as any as FC;
  const { t } = useTranslation();

  return (
    <>
      {hasLtExtras ? (
        <ExtraItems extras={ltExtras} title={t('checkout.services.extras')} />
      ) : null}

      {hasTourExtras ? (
        <ExtraItems
          extras={tourExtras}
          onToggle={toggleExtras}
          title={t('checkout.services.makeComfortable')}
        />
      ) : null}

      {hasTransfer || hasExtras ? (
        <StyledExtrasServiceLayout
          title={t('checkout.services.makeComfortable')}
        >
          {hasTransfer ? (
            <CheckoutTransfers
              transferList={transferList}
              chosenOptions={chosenUpsales}
              uncheckOption={uncheckOption}
              onChange={setOptions}
            />
          ) : null}
          {hasExtras ? (
            <CheckoutExtras
              extras={extrasList}
              chosenOptions={chosenUpsales}
              uncheckOption={uncheckOption}
              onChange={setOptions}
            />
          ) : null}
        </StyledExtrasServiceLayout>
      ) : null}

      <Element name="ensurance-widget" id="ensurance-widget">
        <ExtraInsurance />
      </Element>

      <UpgradeFlightClassComponent />

      <ExtraComment
        reduxComment={comment}
        submit={saveComment}
        isHotelOnly={isHotelOnly}
        isMobile={!!isMobile}
      />
      {!isHotelOnly && <JointOrder />}

      {hasCancellationPolicy ? (
        <StyledCancellationPolicyServiceLayout
          title={t('checkout.hotel.cancellation', {
            context: isHotelOnly ? 'hotel' : 'package',
          })}
          tooltipText={isHotelOnly ? '' : t('checkout.services.cancellation')}
        >
          <CancellationPolicyContainer
            checkout
            insuranceSelected={isSelectedInsurance}
            onEnsuranceBtnClick={ensuranceBtnClickHandler}
            title={t('checkout.hotel.cancellation', {
              context: isHotelOnly ? 'hotel' : 'package',
            })}
            tooltipText={isHotelOnly ? '' : t('checkout.services.cancellation')}
          />
        </StyledCancellationPolicyServiceLayout>
      ) : null}

      <PartnerLoyalty isMobile={isMobile} />
    </>
  );
};

const StyledCancellationPolicyServiceLayout = styled(ServiceLayout)<{
  isMobile?: boolean;
}>`
  margin-top: ${({ isMobile }) => (isMobile ? '16px' : '30px')};
`;

const StyledExtrasServiceLayout = styled(ServiceLayout)`
  margin-bottom: 8px;
`;

export default Services;
